"use client"

import styled from "@emotion/styled"
import { Box, Breadcrumbs, SxProps } from "@mui/material"
import { useTranslations } from "next-intl"
import Link from "next/link"

import NewText from "./NewText/NewText"
import { sharedColors } from "./theme/colors"

// eslint-disable-next-line prefer-destructuring
const ORIGIN = process.env.ORIGIN

const StyledLink = styled.div`
  text-decoration: none;
`

type BreadcrumbType = { title: string | JSX.Element; url?: string; schemaUrl?: string }

type Props = {
  current: BreadcrumbType
  parents: BreadcrumbType[]
  children?: JSX.Element
  noPadding?: boolean
  desktopFontSize?: number
  mobileFontSize?: number
  desktopFontWeight?: string
  mobileFontWeight?: string
  desktopLineHeight?: string
  mobileLineHeight?: string
  sx?: SxProps<any>
}

export const CustomBreadcrumbs: React.FC<Props> = ({
  current,
  parents,
  children,
  noPadding,
  desktopFontSize = 14,
  mobileFontSize = 12,
  desktopFontWeight = "400",
  mobileFontWeight = "400",
  desktopLineHeight = "20px",
  mobileLineHeight = "17px",
  sx,
}) => {
  const t = useTranslations("Accessibility")

  // The schema is rendered during SSR, where window does not exist, so the origin
  // comes from the build time env. Without it every item is emitted as a bare path
  // and search engines reject it, since item has to be an absolute URL.
  const origin = (
    ORIGIN ||
    (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/+$/, "")

  // Trailing slashes are dropped so the URLs match the canonical of each page.
  // The apps run with the Next default, where a trailing slash redirects.
  const toAbsoluteUrl = (path?: string) => {
    if (!path) return undefined
    if (/^https?:\/\//i.test(path)) return path
    if (!origin) return path
    const normalized = path.replace(/\/+$/, "") || "/"
    return `${origin}${normalized.startsWith("/") ? "" : "/"}${normalized}`
  }

  // The current crumb URL comes from the CMS like the parents do, so it already
  // carries the right locale prefix. A bare slash means the caller passed a
  // placeholder, which several of them do because this value used to be unread,
  // and a page that has parents cannot itself be the site root. In that case the
  // item is left off, which stays valid for the last entry.
  const currentPath = current.url || current.schemaUrl
  const currentSchemaPath = currentPath && currentPath !== "/" ? currentPath : undefined

  const breadcrumbEntries = [
    ...parents.map((item) => ({ title: item.title, path: item.url || item.schemaUrl })),
    { title: current.title, path: currentSchemaPath },
  ]

  const breadcrumbItems = breadcrumbEntries
    .map((entry) => {
      const schemaItemUrl = toAbsoluteUrl(entry.path)
      return {
        "@type": "ListItem" as const,
        name: typeof entry.title === "string" ? entry.title : undefined,
        ...(schemaItemUrl ? { item: schemaItemUrl } : {}),
      }
    })
    .filter((entry) => entry.name)
    .map((entry, index) => ({ ...entry, position: index + 1 }))

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems,
  }

  return (
    <Box
      sx={{
        padding: {
          xs: "16px 0",
          md: "20px 0",
        },
        ...sx,
        ...(noPadding && {
          padding: "0",
        }),
      }}
    >
      {breadcrumbItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}
      {children}

      <Breadcrumbs separator={"/"} aria-label={t("breadcrumbNavigation")}>
        {parents.map((item, index) => (
          <StyledLink key={index.toString()}>
            {item.url ? (
              <Link
                style={{
                  textDecoration: "none",
                }}
                href={item.url}
              >
                <NewText
                  component="p"
                  desktopFontSize={desktopFontSize}
                  desktopFontWeight={desktopFontWeight}
                  desktopLineHeight={desktopLineHeight}
                  mobileFontSize={mobileFontSize}
                  mobileFontWeight={mobileFontWeight}
                  mobileLineHeight={mobileLineHeight}
                  color={sharedColors.gray}
                >
                  {item.title}
                </NewText>
              </Link>
            ) : (
              <NewText
                component="p"
                desktopFontSize={desktopFontSize}
                desktopFontWeight={desktopFontWeight}
                desktopLineHeight={desktopLineHeight}
                mobileFontSize={mobileFontSize}
                mobileFontWeight={mobileFontWeight}
                mobileLineHeight={mobileLineHeight}
                color={sharedColors.gray}
              >
                {item.title}
              </NewText>
            )}
          </StyledLink>
        ))}
        <NewText
          component="p"
          desktopFontSize={desktopFontSize}
          desktopFontWeight={desktopFontWeight}
          desktopLineHeight={desktopLineHeight}
          mobileFontSize={mobileFontSize}
          mobileFontWeight={mobileFontWeight}
          mobileLineHeight={mobileLineHeight}
          color={sharedColors.black}
          aria-current="page"
        >
          {current.title}
        </NewText>
      </Breadcrumbs>
    </Box>
  )
}

export default CustomBreadcrumbs
